import React, {
    createContext,
    useContext,
    useEffect,
} from 'react';
import type { ReactNode } from 'react';

import * as amplitude from '@amplitude/analytics-browser';
import { Experiment, Source } from '@amplitude/experiment-js-client';
import { useSelector } from 'react-redux';

import { variantsSelectors } from 'wanderu-analytics';
import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';

import type { EnrichmentPlugin, BrowserConfig, Event } from '@amplitude/analytics-types';

const logger = Logger.getLogger('AmplitudeProvider');

import type { ExperimentClient } from '@amplitude/experiment-js-client';

interface AmplitudeContextType {
    amplitude: typeof amplitude;
    experiment?: ExperimentClient;
}

interface AmplitudeProviderProps {
    children: ReactNode;
}

const AmplitudeContext = createContext<AmplitudeContextType | undefined>(
    undefined
);

class SnowplowIdsPlugin implements EnrichmentPlugin {
    name = 'snowplow-ids-plugin';

    type = 'enrichment' as const;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, class-methods-use-this
    async setup(config: BrowserConfig) {
        return undefined;
    }

    // eslint-disable-next-line class-methods-use-this
    async execute(event: Event) {
        const newEvent = event;

        const {
            domainUserId,
            domainSessionId,
            domainSessionIdx,
        } = SnowplowService.getDomainUserInfo() as {
            domainUserId: string;
            domainSessionId: string;
            domainSessionIdx: number;
        };

        const networkUserId = SnowplowService.getNetworkUserId() as string;

        if (!newEvent.event_properties) {
            newEvent.event_properties = {};
        }

        newEvent.event_properties.domain_session_id = domainSessionId;
        newEvent.event_properties.domain_session_idx = domainSessionIdx;
        newEvent.event_properties.domain_user_id = domainUserId;
        newEvent.event_properties.network_user_id = networkUserId;

        return newEvent;
    }
}

let experiment: ExperimentClient | undefined;

export function AmplitudeProvider({ children }: AmplitudeProviderProps) {
    const variants = useSelector(variantsSelectors.selectEntities);

    useEffect(() => {
        const apiKey = appSettings?.config?.amplitude?.apiKey;

        if (!apiKey) {
            logger.event('Amplitude', 'apiKey not found');

            return;
        }

        amplitude.init(apiKey, { // Browser Analytics https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#configure-the-sdk
            appVersion: appSettings?.env?.build?.version,
            autocapture: {
                attribution: {
                    resetSessionOnNewCampaign: true,
                },
                elementInteractions: true,
                fileDownloads: true,
                formInteractions: true,
                pageViews: true,
                sessions: true,
            },
            serverUrl: appSettings?.config?.amplitude?.serverUrl,
        })
            .promise
            .then(
                (result) => {
                    logger.event('Amplitude', 'loaded', result);
                    amplitude.add(new SnowplowIdsPlugin()); // Add snowplow ids to every Amplitude event

                    const amplitudeDeviceId = amplitude.getDeviceId() as string;

                    SnowplowService.addGlobalContexts([{
                        data: {
                            ampClientId: amplitudeDeviceId,
                        },
                        schema: 'iglu:dev.amp.snowplow/amp_id/jsonschema/1-0-0',
                    }]);
                },
                (error) => {
                    logger.event('Amplitude', 'failed', error);
                }
            );

        if (!experiment) {
            // in the client, we only want to create the ExperimentClient once
            experiment = Experiment.initializeWithAmplitudeAnalytics(apiKey, {
                initialVariants: { ...variants },
                source: Source.InitialVariants,
            });
        }
    }, []);

    return (
        <AmplitudeContext.Provider value={{ amplitude, experiment }}>
            {children}
        </AmplitudeContext.Provider>
    );
}

export function useAmplitude() {
    const context = useContext(AmplitudeContext);

    if (context === undefined) {
        throw new Error('useAmplitude must be used within an AmplitudeProvider');
    }

    return context;
}
